/* RBAC-07 WorkspaceSwitcher (topbar of the sidebar foot). <=1 ws + not org-tier →
   static label (90% case). Multi-ws lead → dropdown + "All my teams" ('all'). Org
   tier → dropdown + "Organization" ('org', labeled "Org view"). Selecting a concrete
   ws switches the session; 'all'/'org' set the context; both re-load builds from the
   correct source (org → /api/org/builds only; 'all' → single-tenant fan-out). */
const _WS_STATIC = { display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: 'var(--muted)', padding: '6px 10px' };
const _WS_WRAP = { position: 'relative', marginBottom: 8 };
const _WS_BTN = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%', padding: '8px 11px', borderRadius: 10, border: 'none', background: 'var(--surface)', boxShadow: 'var(--nm-out-sm)', color: 'var(--ink)', cursor: 'pointer', fontSize: 12.5, fontWeight: 600 };
const _WS_MENU = { position: 'absolute', bottom: 'calc(100% + 6px)', left: 0, right: 0, background: 'var(--surface)', boxShadow: 'var(--nm-out-lg)', borderRadius: 12, padding: 6, zIndex: 50, display: 'flex', flexDirection: 'column', gap: 2, maxHeight: 280, overflowY: 'auto' };
const _WS_OPT = { display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 10px', borderRadius: 8, border: 'none', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontSize: 12.5, textAlign: 'left' };
const _WS_DOT = { width: 8, height: 8, borderRadius: 4, background: 'var(--brand-amber, #C8973F)', flexShrink: 0 };
const _WS_DOT_ORG = { width: 8, height: 8, borderRadius: 4, background: 'var(--running, #3C7DD9)', flexShrink: 0 };
const _WS_ORG_TAG = { marginLeft: 'auto', fontSize: 10, fontWeight: 700, color: 'var(--running)', background: 'var(--running-bg, #EAF0FA)', padding: '1px 6px', borderRadius: 6 };
function WorkspaceSwitcher() {
  const v = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [loadError, setLoadError] = React.useState('');
  if (!v) return null;
  const memberships = v.memberships || [];
  const isOrgTier = v.orgTier != null;
  if (memberships.length <= 1 && !isOrgTier) {            // 90% case → static label, no switcher
    const only = memberships[0];
    return <div style={_WS_STATIC}><span style={_WS_DOT} />{only ? (only.name || only.slug) : 'Workspace'}</div>;
  }
  const curLabel = v.ctx === 'org' ? 'Organization' : v.ctx === 'all' ? 'All my teams'
    : (memberships.find((m) => m.workspaceId === v.ctx) || memberships[0] || {}).name || 'Workspace';
  const pick = async (opt) => {
    setOpen(false); setBusy(true); setLoadError('');
    try {
      const result = await window.GooseSwitchContext(opt);
      if (!result.ok) setLoadError('Workspace could not load. Please retry.');
    } catch (_) { setLoadError('Workspace could not load. Please retry.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={_WS_WRAP}>
      {loadError && <p role="alert">{loadError}</p>}
      <button style={_WS_BTN} disabled={busy} onClick={() => setOpen((o) => !o)} title="Switch workspace / view">
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
          <span style={v.ctx === 'org' ? _WS_DOT_ORG : _WS_DOT} />
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{curLabel}</span>
        </span>
        <I.chevron size={12} style={{ transform: open ? 'rotate(-90deg)' : 'rotate(90deg)', flexShrink: 0 }} />
      </button>
      {open && (
        <div style={_WS_MENU}>
          {isOrgTier
            ? <button style={_WS_OPT} onClick={() => pick('org')}><span style={_WS_DOT_ORG} /> Organization <span style={_WS_ORG_TAG}>Org view</span></button>
            : <button style={_WS_OPT} onClick={() => pick('all')}><span style={_WS_DOT} /> All my teams</button>}
          {memberships.map((m) => (
            <button key={m.workspaceId} style={_WS_OPT} onClick={() => pick(m.workspaceId)}>
              <span style={_WS_DOT} /> {m.name || m.slug}{m.role === 'team_lead' ? ' · lead' : m.role === 'admin' ? ' · admin' : ''}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Sidebar({ route, go, open }) {
  // Live review-queue count — same source the Dashboard uses (dashboard.jsx):
  // prefer the server summary (/api/data/summary → MAV.summary.needs_review),
  // else count needs_review jobs client-side. Recomputed each render (the sidebar
  // re-renders on navigation); 0 → no badge via the `item.badge ?` guard below.
  const _sum = (window.MAV && window.MAV.summary) || {};
  const reviewCount = (_sum.needs_review != null && Number.isFinite(Number(_sum.needs_review)))
    ? Number(_sum.needs_review)
    : ((window.MAV && window.MAV.jobs) || []).filter((j) => j && j.status === 'needs_review').length;

  // Live token-budget fuel gauge — mirrors the MetricSpend hero (metrics.jsx:457)
  // EXACTLY, since this card opens that page (m_spend): real OpenRouter spend vs
  // account balance when the key is live ($26.53 / $30, % burned), else our DB
  // build-cost sum vs the static cap. Credits fetched on mount + cached on
  // MAV.credits — the sidebar is always mounted, so this also warms the cache
  // app-wide. (Was hardcoded $340 / $500 / 68% / "May".)
  const [credits, setCredits] = React.useState((window.MAV && window.MAV.credits) || null);
  React.useEffect(() => {
    let alive = true;
    if (window.GooseAPI && window.GooseAPI.openrouterCredits) {
      window.GooseAPI.openrouterCredits().then((d) => { if (alive && d) { window.MAV.credits = d; setCredits(d); } }).catch(() => {});
    }
    return () => { alive = false; };
  }, []);
  const BUDGET_CAP = 500; // static fallback cap — no workspace-settings table yet
  const _orLive = !!(credits && credits.available && credits.remaining != null);
  const _dbCost = (_sum.total_cost != null) ? Number(_sum.total_cost) : 0;
  const budgetSpent = _orLive ? Number(credits.total_usage) : _dbCost;
  const budgetCap = _orLive ? Number(credits.total_credits) : BUDGET_CAP;
  const budgetPct = budgetCap > 0 ? Math.round((budgetSpent / budgetCap) * 100) : 0;

  // RBAC-07 viewer-derived nav gating. No viewer (pre-hydrate / mock demo) → fail
  // OPEN for the nav (the SERVER 403s the real /api/admin/* + /api/org/* calls); a
  // live viewer drives real gating. Sidebar re-renders via App's goose-rbac-change
  // subscription, so reading window.RBAC.viewer() per render stays current.
  const _v = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
  const seeAdmin = _v ? _v.canSeeAdminDesk : true;        // admin | PM (orgTier != null)
  const startBuild = _v ? _v.canStartBuild : true;        // a QA-only seat can't start builds
  const leadMembers = !!(_v && _v.canManageMembers && !_v.canSeeAdminDesk);  // pod_lead → "Team members"

  const nav1 = [
    { id: 'home', label: 'Dashboard', ico: I.home },
    { id: 'builds', label: 'Builds', ico: I.builds },
    { id: 'queue', label: 'Review queue', ico: I.review, badge: reviewCount },
  ];
  const nav2 = [
    { id: 'kb', label: 'Knowledge base', ico: I.kb },
    // ORG-04: variant-source submit + SME approval queue (shared-matrix gated; the
    // server enforces regardless — hiding is UX, not security).
    ...((!_v || window.RBAC.requireRole('upload', _v))
      ? [{ id: 'profilesrc', label: 'Profile sources', ico: I.check }] : []),
    ...((window.VariantsDash && ((!window.MAV.__live && !_v) || window.RBAC.requireRole('upload', _v)))
      ? [{ id: 'variants', label: 'Variants', ico: I.layers }] : []),
    // RBAC-07: Usage + Admin desk are admin/PM only; a pod_lead gets a scoped
    // "Team members" item instead; builders/QA see neither.
    ...(seeAdmin
      ? [{ id: 'usage', label: 'Usage & budgets', ico: I.usage }, { id: 'admin', label: 'Admin desk', ico: I.shield }]
      : leadMembers
        ? [{ id: 'members', label: 'Team members', ico: I.users }]
        : []),
    { id: 'settings', label: 'Settings', ico: I.settings },
  ];
  const NavItem = ({ item }) => (
    <button className={'nav-item' + (route === item.id ? ' active' : '')} onClick={() => go(item.id)}>
      <span className="nav-ico"><item.ico size={18} /></span>
      {item.label}
      {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
    </button>
  );

  return (
    <aside className={'sidebar' + (open ? ' open' : '')}>
      <button className="brand-btn" onClick={() => go('home')} title="Go to dashboard">
        <div className="brand-mark goose" title="Every Maverick needs a Goose."><GooseMark size={27} /></div>
        <div>
          <div className="brand-name">Goose</div>
          <div className="brand-sub">Email build squadron</div>
        </div>
      </button>

      {startBuild && (
        <button className="btn btn-primary btn-lg" style={{ justifyContent: 'center', margin: '0 6px' }} onClick={() => go('new')}>
          <I.add size={17} /> New build
        </button>
      )}

      <div>
        <div className="nav-group-label">Workspace</div>
        <nav className="nav">{nav1.map(i => <NavItem key={i.id} item={i} />)}</nav>
      </div>
      <div>
        <div className="nav-group-label">Library & admin</div>
        <nav className="nav">{nav2.map(i => <NavItem key={i.id} item={i} />)}</nav>
      </div>

      <div className="sidebar-foot">
        <WorkspaceSwitcher />
        {/* VD-01/PY-22: codex-default workspace → the compact codex session tile replaces the
            $ fuel gauge (same RBAC.activeEngine gate as the dashboard). */}
        {(window.RBAC && window.RBAC.activeEngine && window.RBAC.activeEngine() === 'codex' && window.CodexSessionTile)
          ? <CodexSessionTile go={go} />
          : <button className="budget-card" onClick={() => go('m_spend')} title="View token spend — keep something in the tank, Maverick.">
          <div className="row between">
            <span className="lbl">Token budget</span>
            <span className="val">{budgetPct}%</span>
          </div>
          <div className={'meter' + (budgetPct >= 90 ? ' warn' : '')}><span style={{ width: Math.min(100, budgetPct) + '%' }}></span></div>
          <div className="row between">
            <span className="val muted" style={{ fontSize: 11 }}>${budgetSpent.toFixed(2)} / ${budgetCap.toFixed(0)}</span>
            <span className="val muted" style={{ fontSize: 11 }}>{_orLive ? 'live' : 'lifetime'}</span>
          </div>
        </button>}
        {(() => {
          // Live user from MAV.currentUser (set by hydrate's /me fetch); fall
          // back to the mock 'jd' lookup so the demo flow without a server
          // still renders the original Jordan Diaz chip.
          const cu = window.MAV.currentUser;
          const memberExt = cu ? (cu.email && cu.email.split('@')[0]) || 'jd' : 'jd';
          const name = cu ? cu.name : 'Jordan Diaz';
          const initials = cu ? cu.initials : 'JD';
          const role = cu ? ((window.MAV.team || []).find((m) => m.id === memberExt) || {}).role || 'Member' : 'Producer';
          const team = (window.MAV.teamForMember(memberExt) || {}).name || 'Mavlers';
          const path = (window.MAV.memberPath(memberExt) || {}).str || 'Mavlers';
          return (
            <button className={'user-chip clickable' + (route === 'profile' ? ' active' : '')} onClick={() => go('profile')} title="Account & settings" data-user-chip={cu ? 'live' : 'mock'}>
              <div className="avatar" title={'Callsign: Maverick · ' + name}>{initials}</div>
              <div style={{ minWidth: 0 }}>
                <div className="nm">{name}</div>
                <div className="rl" title={path}>{role} · {team}</div>
              </div>
            </button>
          );
        })()}
      </div>
    </aside>
  );
}

/* Global search (topbar). Was a dead placeholder input with a decorative ⌘K hint — both
   are live now. Searches the two things the placeholder promises: builds and client
   profiles. Selecting a result navigates; ⌘K focuses; ↑/↓/Enter/Esc work. */
const SB_MAX = 8;

function sbResults(q, canSeeIssues) {
  const needle = q.trim().toLowerCase();
  if (!needle) return [];
  const MAV = window.MAV || {};
  const has = (...vals) => vals.some((v) => String(v == null ? '' : v).toLowerCase().includes(needle));
  // exact-prefix matches first — typing "syn" should surface Synchrony above a build that
  // merely mentions it.
  const rank = (label) => (String(label || '').toLowerCase().startsWith(needle) ? 0 : 1);
  const out = [];
  for (const j of MAV.jobs || []) {
    if (!has(j.name, j.id, j.client, j.status, j.verdict, j.owner)) continue;
    const c = MAV.clientBySlug ? MAV.clientBySlug(j.client) : null;
    out.push({
      kind: 'build', id: j.id, label: j.name || j.id,
      sub: [c ? c.name : j.client, String(j.status || '').replace(/_/g, ' ')].filter(Boolean).join(' · '),
      r: rank(j.name || j.id),
    });
  }
  for (const c of MAV.clients || []) {
    if (!has(c.name, c.slug, c.esp, (c.flags || []).join(' '))) continue;
    out.push({
      kind: 'client', id: c.slug, label: c.name,
      sub: [c.slug, c.esp].filter(Boolean).join(' · '),
      r: rank(c.name),
    });
  }
  // Issues live in the admin desk and stay there: only surfaced when RBAC explicitly says
  // this viewer sees it. Deliberately FAIL-CLOSED — unlike the sidebar's nav gate, which
  // defaults open when RBAC is absent. A hidden nav item is cosmetic; issue titles in a
  // builder's search box would be content disclosure. (Dev note: VITE_MOCK_AUTH=1 skips the
  // /me probe entirely, so viewer() is null and issues are correctly absent locally.)
  if (canSeeIssues) {
    for (const it of MAV.issues || []) {
      if (!has(it.id, it.title, it.build, it.type, it.status, it.sev, (it.labels || []).join(' '))) continue;
      out.push({
        kind: 'issue', id: it.id, label: it.title || it.id,
        sub: [it.id, it.build, it.sev].filter(Boolean).join(' · '),
        r: rank(it.title || it.id),
      });
    }
  }
  return out.sort((a, b) => a.r - b.r || String(a.label).localeCompare(String(b.label))).slice(0, SB_MAX);
}

function GlobalSearch({ openJob, openClient, openIssue }) {
  const [q, setQ] = React.useState('');
  const [sel, setSel] = React.useState(0);
  // Open-ness is derived from the QUERY plus an explicit dismissal — not from focus.
  // Tracking `focused` looked equivalent but silently closed the menu whenever the tree
  // re-rendered with the field already focused (the live case: RBAC's viewer arriving from
  // /api/auth/me mid-typing), because onFocus never fires again on an already-focused input.
  const [dismissed, setDismissed] = React.useState(false);
  const boxRef = React.useRef(null);
  const inputRef = React.useRef(null);
  // Hangman's RBAC-07 viewer is the authority; it arrives asynchronously from /api/auth/me,
  // so re-read it on rbac.js's 'goose-rbac-change' rather than snapshotting once at mount
  // (a wrong event name here fails SILENTLY: the gate would never flip true for admins).
  const [canSeeIssues, setCanSeeIssues] = React.useState(false);
  React.useEffect(() => {
    const read = () => {
      const v = (window.RBAC && window.RBAC.viewer && window.RBAC.viewer()) || null;
      setCanSeeIssues(!!(v && v.canSeeAdminDesk));
    };
    read();
    window.addEventListener('goose-rbac-change', read);
    return () => window.removeEventListener('goose-rbac-change', read);
  }, []);
  const results = React.useMemo(() => sbResults(q, canSeeIssues), [q, canSeeIssues]);
  const open = q.trim().length > 0 && !dismissed;

  React.useEffect(() => { setSel(0); }, [q]);

  // ⌘K / Ctrl-K — the kbd hint has been on screen since launch promising this.
  React.useEffect(() => {
    const h = (e) => {
      if ((e.metaKey || e.ctrlKey) && String(e.key).toLowerCase() === 'k') {
        e.preventDefault();
        if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // click-away closes the list but keeps what was typed
  React.useEffect(() => {
    if (!open) return undefined;
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setDismissed(true); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const choose = (item) => {
    if (!item) return;
    setQ(''); setDismissed(false);
    if (inputRef.current) inputRef.current.blur();
    if (item.kind === 'build') { if (openJob) openJob(item.id); }
    else if (item.kind === 'issue') { if (openIssue) openIssue(item.id); }
    else if (openClient) openClient(item.id);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setDismissed(true); e.currentTarget.blur(); return; }
    if (!results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((v) => (v + 1) % results.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((v) => (v - 1 + results.length) % results.length); }
    else if (e.key === 'Enter') { e.preventDefault(); choose(results[sel]); }
  };

  return (
    <div className="searchbox" ref={boxRef}>
      <I.search size={15} />
      <input
        ref={inputRef}
        value={q}
        placeholder="Search builds, clients…"
        onChange={(e) => { setQ(e.target.value); setDismissed(false); }}
        onFocus={() => setDismissed(false)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={open}
        aria-controls="sb-results"
        aria-autocomplete="list"
        aria-label="Search builds and clients"
      />
      <kbd>⌘K</kbd>
      {open && (
        <div className="sb-menu" id="sb-results" role="listbox">
          {results.length === 0
            ? <div className="sb-empty">No builds or clients match “{q.trim()}”.</div>
            : results.map((item, i) => (
              <button
                key={item.kind + ':' + item.id}
                className={'sb-opt' + (i === sel ? ' sel' : '')}
                role="option"
                aria-selected={i === sel}
                onMouseEnter={() => setSel(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => choose(item)}
              >
                <span className="sb-ico">{item.kind === 'build' ? <I.builds size={14} /> : item.kind === 'issue' ? <I.flag size={14} /> : <I.kb size={14} />}</span>
                <span className="sb-text">
                  <span className="sb-label">{item.label}</span>
                  <span className="sb-sub mono">{item.sub}</span>
                </span>
                <span className="sb-kind">{item.kind}</span>
              </button>
            ))}
        </div>
      )}
    </div>
  );
}

function Topbar({ crumbs, go, route, onMenu, openJob, openClient, openIssue }) {
  const [theme, setTheme] = React.useState(() => window.GooseTheme.get());
  React.useEffect(() => {
    const h = (e) => setTheme(e.detail);
    window.addEventListener('goose-theme-change', h);
    return () => window.removeEventListener('goose-theme-change', h);
  }, []);
  const cycleTheme = () => {
    const T = window.GooseTheme.THEMES;
    window.GooseTheme.set(T[(T.indexOf(theme) + 1) % T.length]);
  };
  const themeIco = theme === 'light' ? <I.sun size={17} /> : theme === 'dark' ? <I.moon size={17} /> : <I.terminal size={17} />;
  const nextLabel = window.GooseTheme.labels[window.GooseTheme.THEMES[(window.GooseTheme.THEMES.indexOf(theme) + 1) % 3]];
  const [sndOn, setSndOn] = React.useState(() => !(window.GooseSound && window.GooseSound.muted));
  const toggleSnd = () => { if (window.GooseSound) setSndOn(window.GooseSound.toggle()); };
  return (
    <div className="topbar">
      <button className="nav-toggle" onClick={onMenu} title="Menu" aria-label="Open navigation"><I.menu size={18} /></button>
      <div className="crumbs">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <span className="sep">/</span>}
            {c.b ? <b>{c.t}</b>
              : c.r && go ? <button onClick={() => go(c.r)}>{c.t}</button>
              : <span>{c.t}</span>}
          </React.Fragment>
        ))}
      </div>
      <div className="topbar-spacer"></div>
      <GlobalSearch openJob={openJob} openClient={openClient} openIssue={openIssue} />
      <button className="icon-btn theme-toggle" onClick={cycleTheme} title={'Theme: ' + window.GooseTheme.labels[theme] + ' · switch to ' + nextLabel} aria-label="Cycle theme">
        {themeIco}
      </button>
      <button className="icon-btn" onClick={toggleSnd} title={sndOn ? 'UI sound: on · click to mute' : 'UI sound: muted · click to unmute'} aria-label="Toggle UI sound">
        {sndOn ? <I.sound size={17} /> : <I.mute size={17} />}
      </button>
      <button className="icon-btn" title="Notifications"><I.bell size={17} /></button>
      {(() => {
        const cu = window.MAV.currentUser;
        const initials = cu ? cu.initials : 'JD';
        const title = cu ? `${cu.name} · Account & settings` : 'Callsign: Maverick · Account & settings';
        return (
          <button className={'topbar-avatar' + (route === 'profile' ? ' active' : '')} onClick={() => go && go('profile')} title={title} data-topbar-avatar={cu ? 'live' : 'mock'}>
            <span className="avatar">{initials}</span>
          </button>
        );
      })()}
    </div>
  );
}

Object.assign(window, { Sidebar, Topbar });
